import DiagnosticsModule, {
  MetricNames,
  MetricTags,
} from '@modules/DiagnosticsModule';

/**
 * Metrics reported when an incident starts
 */
interface IncidentStartMetrics {
  /** Queue name identifier */
  queue: string;
  /** Current number of events in queue right now */
  currentDepth: number;
  /** Peak queue depth reached during this incident */
  maxDepth: number;
  /** Highest queue depth ever reached since workspace opened */
  allTimeMaxDepth: number;
  /** Average queue depth over time (from samples) */
  avgDepth: number;
  /** Alert threshold (30) - queue must exceed this for 5s to trigger incident */
  threshold: number;
  /** Total events added to queue since start */
  eventsReceived: number;
  /** Total events completed since start */
  eventsProcessed: number;
  /** Events still pending (received but not processed) = eventsReceived - eventsProcessed */
  backlog: number;
  /** Average time to process one event (in milliseconds) */
  avgProcessingDurationMs: number;
  /** Number of processing duration samples used to calculate avgProcessingDurationMs (max 500) */
  processingDurationSamples: number;
}

/**
 * Metrics reported when an incident recovers
 */
interface IncidentRecoveryMetrics {
  /** Queue name identifier */
  queue: string;
  /** Total time from incident start to recovery (milliseconds) */
  recoveryTimeMs: number;
  /** Queue depth when incident started */
  startDepth: number;
  /** Peak queue depth reached during this incident */
  maxDepth: number;
  /** Highest queue depth ever reached since workspace opened */
  allTimeMaxDepth: number;
  /** Current queue depth (now below threshold) */
  currentDepth: number;
  /** Average queue depth over time */
  avgDepth: number;
  /** Alert threshold (30) */
  threshold: number;
  /** Total events added to queue */
  eventsReceived: number;
  /** Total events completed */
  eventsProcessed: number;
  /** Events still pending */
  backlog: number;
  /** Average time to process one event (ms) */
  avgProcessingDurationMs: number;
  /** Number of processing duration samples used to calculate avgProcessingDurationMs (max 500) */
  processingDurationSamples: number;
}

/**
 * EventQueueMetrics tracks queue performance and reports to monitoring systems.
 * Provides insights into queue depth, processing times, and throughput.
 *
 * ALERTING STRATEGY:
 * ------------------
 * Initial Detection:
 *   - Queue depth ≥30 for 5 consecutive seconds → Incident started
 *   - Avoids false alarms from brief spikes
 *
 * Escalation Reminders (if incident persists):
 *   - Reminder 1: After 5 minutes still breached
 *   - Reminder 2: After 20 minutes total (15 min since last)
 *   - Reminder 3: After 50 minutes total (30 min since last)
 *   - Max 3 reminders (assumes team is aware and working on it)
 *
 * Recovery:
 *   - Queue depth <30 → Incident resolved immediately
 *   - Recovery alert always sent (confirms fix worked)
 *   - New incidents after recovery alert immediately (no cooldown)
 *
 * METRICS EXPLANATION:
 * -------------------
 * Queue Depth Metrics (how backed up is the queue?):
 *   - currentDepth: Events waiting right now (67 = 67 events in queue)
 *   - maxDepth: Peak depth during incident (100 = queue peaked at 100)
 *   - avgDepth: Average depth over time (50.50 = averaged ~50 events)
 *   - threshold: Alert trigger level (30 = alert when ≥30 for 5+ seconds)
 *
 * Throughput Metrics (event flow rates):
 *   - eventsReceived: Total events queued (100 = 100 events added)
 *   - eventsProcessed: Total events completed (33 = 33 finished)
 *   - backlog: Pending events (67 = 67 still waiting, calculated as received - processed)
 *
 * Performance Metrics (processing speed):
 *   - avgProcessingDurationMs: Time per event (152.48ms = ~152ms per event)
 *   - recoveryTimeMs: Incident duration (only in recovery alert)
 *
 * INTERPRETING THE METRICS:
 * -------------------------
 * Example: avgDepth=50.50, backlog=67, avgProcessingDurationMs=152.48
 *   → Queue has 67 events pending
 *   → Averaged ~50 events over time (growing)
 *   → Each event takes ~152ms to process
 *   → At 152ms/event, will take ~10 seconds to clear remaining 67 events
 *   → This is a genuine congestion incident, not a false alarm
 */
export class EventQueueMetrics {
  private readonly queueName: string;
  private enabled = false; // Metrics only active when feature flag is enabled

  // Queue depth tracking
  private currentDepth = 0;
  private maxDepth = 0;
  private allTimeMaxDepth = 0; // Never reset - tracks highest depth since workspace opened
  private depthSamples: number[] = [];

  // Processing time tracking
  private readonly processingStartTimes = new Map<string, number>();
  private processingDurations: number[] = [];

  // Throughput tracking
  private eventsProcessedCount = 0;
  private eventsReceivedCount = 0;

  // Threshold-based alerting
  // At ~17 events/sec (1035 users), 30 items = ~1.7s backlog (reasonable alert threshold)
  private readonly ALERT_THRESHOLD = 30; // Only alert when queue >= 30
  private readonly SUSTAINED_BREACH_MS = 5000; // Queue must be stuck for 5 seconds before alerting

  // Periodic metrics reporting
  private readonly METRICS_REPORT_INTERVAL_MS = 60 * 1000; // Report queue depth every 60 seconds
  private lastMetricsReportTime = 0; // Track when we last reported metrics

  // Escalation reminder configuration
  private readonly REMINDER_INTERVALS_MS = [
    5 * 60 * 1000, // 5 minutes - First reminder
    15 * 60 * 1000, // 15 minutes - Second reminder (20 min total)
    30 * 60 * 1000, // 30 minutes - Third reminder (50 min total)
  ];
  private readonly MAX_REMINDERS = 3; // Stop after 3 reminders (assume team is aware)

  // Incident lifecycle tracking
  private incidentState: 'healthy' | 'breach' = 'healthy';
  private incidentStartTime: number | null = null;
  private incidentStartDepth = 0;
  private incidentStartAlertSent = false; // Track if start alert was actually sent
  private reminderCount = 0; // Track how many reminders sent for current incident
  private lastReminderTime = 0; // Track when last reminder was sent
  private firstBreachTime: number | null = null; // When queue first exceeded threshold

  constructor(queueName: string) {
    this.queueName = queueName;
  }

  /**
   * Enable metrics tracking (when feature flag is on)
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Disable metrics tracking (when feature flag is off)
   */
  disable(): void {
    this.enabled = false;
    // Reset state when disabling
    this.incidentState = 'healthy';
    this.incidentStartTime = null;
    this.incidentStartAlertSent = false;
    this.reminderCount = 0;
    this.lastReminderTime = 0;
  }

  /**
   * Build incident start metrics object
   */
  private buildIncidentStartMetrics(): IncidentStartMetrics {
    const avgDepth = this.calculateAverage(this.depthSamples);
    const avgProcessingDuration = this.calculateAverage(
      this.processingDurations,
    );

    return {
      queue: this.queueName,
      currentDepth: this.currentDepth,
      maxDepth: this.maxDepth,
      allTimeMaxDepth: this.allTimeMaxDepth,
      avgDepth: avgDepth,
      threshold: this.ALERT_THRESHOLD,
      eventsReceived: this.eventsReceivedCount,
      eventsProcessed: this.eventsProcessedCount,
      backlog: this.eventsReceivedCount - this.eventsProcessedCount,
      avgProcessingDurationMs: avgProcessingDuration,
      processingDurationSamples: this.processingDurations.length,
    };
  }

  /**
   * Build incident recovery metrics object
   */
  private buildIncidentRecoveryMetrics(
    recoveryTime: number,
    startDepth: number,
    maxDepth: number,
  ): IncidentRecoveryMetrics {
    const avgDepth = this.calculateAverage(this.depthSamples);
    const avgProcessingDuration = this.calculateAverage(
      this.processingDurations,
    );

    return {
      queue: this.queueName,
      recoveryTimeMs: recoveryTime,
      startDepth,
      maxDepth,
      allTimeMaxDepth: this.allTimeMaxDepth,
      currentDepth: this.currentDepth,
      avgDepth: avgDepth,
      threshold: this.ALERT_THRESHOLD,
      eventsReceived: this.eventsReceivedCount,
      eventsProcessed: this.eventsProcessedCount,
      backlog: this.eventsReceivedCount - this.eventsProcessedCount,
      avgProcessingDurationMs: avgProcessingDuration,
      processingDurationSamples: this.processingDurations.length,
    };
  }

  /**
   * Check if a minute has passed since last metrics report and send queue depth metric.
   * Called on each event to avoid using setInterval.
   */
  private checkAndReportPeriodicMetrics(): void {
    if (!this.enabled) {
      return;
    }

    const now = Date.now();
    const timeSinceLastReport = now - this.lastMetricsReportTime;

    // Report metrics every minute
    if (timeSinceLastReport >= this.METRICS_REPORT_INTERVAL_MS) {
      this.lastMetricsReportTime = now;

      // Report current queue depth
      DiagnosticsModule.recordValue(MetricNames.QUEUE_SIZE, this.currentDepth, {
        [MetricTags.QUEUE_NAME]: this.queueName,
      });
    }
  }

  /**
   * Check for state transitions and send metrics when threshold is crossed.
   * Incidents always alert immediately. Reminders sent at escalating intervals (5min, 20min, 50min).
   */
  private checkStateTransition(): void {
    // Only check transitions if metrics are enabled (feature flag is on)
    if (!this.enabled) {
      return;
    }

    const now = Date.now();

    // Track when queue first breaches threshold
    if (this.currentDepth >= this.ALERT_THRESHOLD && !this.firstBreachTime) {
      this.firstBreachTime = now;
    }

    // Reset breach tracking if queue drains before sustained period
    if (this.currentDepth < this.ALERT_THRESHOLD) {
      this.firstBreachTime = null;
    }

    // Healthy → Breach transition (only after sustained breach - queue not draining)
    if (
      this.incidentState === 'healthy' &&
      this.currentDepth >= this.ALERT_THRESHOLD &&
      this.firstBreachTime &&
      now - this.firstBreachTime >= this.SUSTAINED_BREACH_MS
    ) {
      // Always transition state
      this.incidentState = 'breach';
      this.incidentStartTime = now;
      this.incidentStartDepth = this.currentDepth;
      this.incidentStartAlertSent = true;
      this.reminderCount = 0; // Reset reminder count for new incident
      this.lastReminderTime = now; // Track when incident started

      // Build metrics object (descriptions defined in IncidentStartMetrics interface)
      const metrics = this.buildIncidentStartMetrics();

      // Report incident to Sentry
      const message = `EventQueueMetrics: Queue incident started: ${this.queueName} queue depth (${this.currentDepth}) exceeded threshold (${this.ALERT_THRESHOLD})`;
      DiagnosticsModule.reportUserError(message, {
        source: 'EventQueueMetrics',
        priority: 'normal',
        extra: metrics,
      });
    }

    // Escalation reminders (if incident is ongoing and not recovering)
    if (
      this.incidentState === 'breach' &&
      this.currentDepth >= this.ALERT_THRESHOLD &&
      this.incidentStartAlertSent &&
      this.reminderCount < this.MAX_REMINDERS
    ) {
      const timeSinceLastReminder = now - this.lastReminderTime;
      const nextReminderInterval =
        this.REMINDER_INTERVALS_MS[this.reminderCount];

      if (timeSinceLastReminder >= nextReminderInterval) {
        this.reminderCount++;
        this.lastReminderTime = now;

        // Build metrics object
        const metrics = this.buildIncidentStartMetrics();
        const incidentDuration = now - this.incidentStartTime!;

        // Report reminder to Sentry
        const message = `EventQueueMetrics: Queue incident ongoing (Reminder ${this.reminderCount}/${this.MAX_REMINDERS}): ${this.queueName} queue depth (${this.currentDepth}) still above threshold after ${Math.floor(incidentDuration / 1000)}s`;
        DiagnosticsModule.reportUserError(message, {
          source: 'EventQueueMetrics',
          priority: 'normal',
          extra: {
            ...metrics,
            reminderNumber: this.reminderCount,
            incidentDurationMs: incidentDuration,
          },
        });
      }
    }

    // Breach → Healthy transition
    if (
      this.incidentState === 'breach' &&
      this.currentDepth < this.ALERT_THRESHOLD
    ) {
      const recoveryTime = now - this.incidentStartTime!;
      const maxDepth = this.maxDepth;
      const startDepth = this.incidentStartDepth;

      // Always transition state (not rate-limited)
      this.incidentState = 'healthy';
      this.firstBreachTime = null; // Reset breach tracking on recovery

      // Only send recovery alert if start alert was sent
      if (this.incidentStartAlertSent) {
        // Build metrics object (descriptions defined in IncidentRecoveryMetrics interface)
        const metrics = this.buildIncidentRecoveryMetrics(
          recoveryTime,
          startDepth,
          maxDepth,
        );

        // Report recovery to Sentry
        const message = `EventQueueMetrics: Queue incident recovered: ${this.queueName} queue recovered in ${recoveryTime}ms (peak: ${maxDepth})`;
        DiagnosticsModule.reportUserError(message, {
          source: 'EventQueueMetrics',
          priority: 'normal',
          extra: metrics,
        });
      }

      // Reset flags, reminder tracking, and max depth after recovery
      this.incidentStartAlertSent = false;
      this.reminderCount = 0;
      this.lastReminderTime = 0;
      this.maxDepth = this.currentDepth;
    }
  }

  /**
   * Record that an event was received and queued
   */
  recordEventQueued(_eventType?: string): void {
    // Only record metrics if enabled (feature flag is on)
    if (!this.enabled) {
      return;
    }

    this.eventsReceivedCount++;
    this.currentDepth++;

    // Track max depth (reset on recovery)
    if (this.currentDepth > this.maxDepth) {
      this.maxDepth = this.currentDepth;
    }

    // Track all-time max depth (never reset)
    if (this.currentDepth > this.allTimeMaxDepth) {
      this.allTimeMaxDepth = this.currentDepth;
    }

    // Sample depth for average calculation
    this.depthSamples.push(this.currentDepth);

    // Limit sample size to prevent memory growth
    if (this.depthSamples.length > 1000) {
      this.depthSamples = this.depthSamples.slice(-500);
    }

    // Check for state transitions
    this.checkStateTransition();

    // Check and report periodic metrics (every minute)
    this.checkAndReportPeriodicMetrics();
  }

  /**
   * Record that event processing started
   */
  recordProcessingStarted(eventId: string): void {
    // Only record metrics if enabled (feature flag is on)
    if (!this.enabled) {
      return;
    }

    this.processingStartTimes.set(eventId, Date.now());

    // Prevent memory leak: if map grows too large (events not completing),
    // remove oldest entries. This should never happen in normal operation.
    if (this.processingStartTimes.size > 1000) {
      const now = Date.now();
      const staleThresholdMs = 5 * 60 * 1000; // 5 minutes

      // Remove entries older than 5 minutes (events that never completed)
      for (const [id, startTime] of this.processingStartTimes.entries()) {
        if (now - startTime > staleThresholdMs) {
          this.processingStartTimes.delete(id);
        }
      }
    }
  }

  /**
   * Record that event processing completed
   */
  recordProcessingCompleted(eventId: string, _eventType?: string): void {
    // Only record metrics if enabled (feature flag is on)
    if (!this.enabled) {
      return;
    }

    const startTime = this.processingStartTimes.get(eventId);
    if (startTime) {
      const duration = Date.now() - startTime;
      this.processingDurations.push(duration);
      this.processingStartTimes.delete(eventId);

      // Limit sample size
      if (this.processingDurations.length > 1000) {
        this.processingDurations = this.processingDurations.slice(-500);
      }
    }

    this.eventsProcessedCount++;
    this.currentDepth--;

    // Check for state transitions
    this.checkStateTransition();

    // Check and report periodic metrics (every minute)
    this.checkAndReportPeriodicMetrics();
  }

  /**
   * Calculate average of an array
   */
  private calculateAverage(values: number[]): number {
    if (values.length === 0) return 0;
    const sum = values.reduce((a, b) => a + b, 0);
    return sum / values.length;
  }
}
